import { Controller } from "@hotwired/stimulus"

const COLLAPSED = "message-card--collapsed"

// The middle of a long thread opens as one-line summaries (messages/_summary, FRO-133). Lives on
// the thread rather than on each summary, so a thread of forty collapsed rows connects one
// controller, not forty.
export default class extends Controller {
  connect() {
    this.expanding = new Map()
  }

  // The card arrives inside the summary's frame, and it is the same <article id="message_N"> the
  // summary already is. Rendered the default way it would nest inside the row with that id twice,
  // so the card takes the row's place instead and the thread ends up exactly as if it had opened
  // with this message expanded.
  //
  // The card's top is pinned to where the row's was. The row is often the node the browser's own
  // scroll anchoring has chosen, and once it leaves the document Chrome re-anchors below it — so
  // the card grew upward and the reader lost the line they had just clicked.
  swap(event) {
    const summary = event.target.closest(`.${COLLAPSED}`)
    if (!summary) return

    event.detail.render = (_frame, newFrame) => {
      const card = newFrame.querySelector(".message-card")
      if (!card) return

      const scroller = summary.closest(".thread__scroll")
      const top = summary.getBoundingClientRect().top
      summary.replaceWith(card)
      if (scroller) scroller.scrollTop += card.getBoundingClientRect().top - top

      this.expanding.get(card.id)?.(card)
      this.expanding.delete(card.id)
    }
  }

  // For callers that need the card rather than a click on it (comment-anchor#reveal). Resolves
  // with the card once it has taken the summary's place. Settled from #swap rather than from
  // turbo:frame-render, because by then the frame is out of the document and Turbo dispatches that
  // event on <html> instead, with nothing on it to say which frame it was.
  expand(summary) {
    const frame = summary.querySelector("turbo-frame")
    const link = frame?.querySelector("a[href]")
    if (!link) return Promise.resolve(summary)

    return new Promise((resolve) => {
      this.expanding.set(summary.id, resolve)
      frame.src = link.href
    })
  }

  // A morph refresh re-renders the thread from the server, which knows nothing of what this reader
  // opened — nor that a card which was among the newest three has since been pushed up by a reply.
  // Neither should fold shut under them, so an expanded card is never morphed into a summary.
  keepExpanded(event) {
    const { currentElement, newElement } = event.detail
    if (!(currentElement instanceof Element) || !(newElement instanceof Element)) return
    if (!currentElement.classList.contains("message-card") || currentElement.classList.contains(COLLAPSED)) return
    if (!newElement.classList.contains(COLLAPSED) || newElement.id !== currentElement.id) return

    event.preventDefault()
  }
}
